import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useErrorNotice } from '../../../hooks/useErrorNotice';
import { makeStyles } from '@material-ui/core/styles';
import { IconButton, Tooltip, CircularProgress } from '@material-ui/core';
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera';
import { APP_ROOT_SELECTOR, captureElement } from '../../../common/captureElement';

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Mirrors `SessionExportButton`'s
 * spacing so the two icons sit evenly next to each other in the NavigationBar.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Core
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  icon: {
    marginRight: theme.spacing(1),
  },
}));

/**
 * @function ScreenshotButton
 * @description Saves the current result screen as a PNG. Renders the app's root element to a
 * canvas with `html2canvas` - the same library MepBay already uses for its map exports - and hands
 * the result to the browser as a download.
 * `useCORS` lets the capture include the Google Maps tiles, which are served cross-origin; tiles
 * that the browser refuses to release still come out blank, which is a limitation of capturing
 * third-party imagery from the page rather than something this component can work around.
 * Nothing here changes what is on screen - the capture is taken from an offscreen copy of the
 * document, and the one style tweak it needs is applied to that copy through `onclone`.
 * Sits beside `SessionExportButton` and appears under the same condition, since both are ways of
 * taking a finished result away from the screen.
 * **Called by:
 * @see {NavigationBar}
 * @memberof module:Core
 * @returns {JSX.Element}
 */
const ScreenshotButton = () => {
  const classes = useStyles();
  const [t] = useTranslation();
  /**
   * Problems are reported in the corner instead of in a blocking dialog - see {@link useErrorNotice}.
   */
  const notifyError = useErrorNotice();
  const [capturing, setCapturing] = useState(false);

  const handleCapture = async () => {
    const root = document.querySelector(APP_ROOT_SELECTOR);
    if (!root) return;

    setCapturing(true);
    try {
      const canvas = await captureElement(root);
      const link = document.createElement('a');
      link.download = `cassie-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      notifyError(t('forms.core.screenshot.error'));
    } finally {
      setCapturing(false);
    }
  };

  return (
    <Tooltip title={t('forms.core.screenshot.button')}>
      <span>
        <IconButton size="medium" className={classes.icon} disabled={capturing} onClick={handleCapture}>
          {capturing ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <PhotoCameraIcon fontSize="inherit" color="primary" />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default ScreenshotButton;
